/**
 * Email Service - Resend Integration
 * 
 * Handles all email sending via Resend API
 */

import { ServiceResponse, successResponse, errorResponse, handleServiceError, notConfiguredError } from '@/lib/service-errors';

export interface EmailOptions {
  to: string | string[];
  from?: string;
  subject: string;
  html?: string;
  text?: string;
  replyTo?: string;
  cc?: string | string[];
  bcc?: string | string[];
}

export interface EmailTemplate {
  subject: string;
  html: string;
  text?: string;
}

/**
 * Check if Resend is configured
 */
const isResendConfigured = (): boolean => {
  return typeof import.meta.env.VITE_RESEND_API_KEY === 'string' && 
         import.meta.env.VITE_RESEND_API_KEY.length > 0;
};

/**
 * Get Resend API key
 */
const getResendApiKey = (): string => {
  const key = import.meta.env.VITE_RESEND_API_KEY;
  if (!key || typeof key !== 'string') {
    throw notConfiguredError('Resend API Key');
  }
  return key;
};

/**
 * Send email via Resend API
 */
export const sendEmail = async (options: EmailOptions): Promise<ServiceResponse<void>> => {
  if (!isResendConfigured()) {
    console.warn('[emailService] Resend API key not configured - email sending disabled');
    return errorResponse(notConfiguredError('Resend API Key'));
  }

  try {
    const apiKey = getResendApiKey();
    const fromEmail = options.from || import.meta.env.VITE_RESEND_FROM_EMAIL || 'onboarding@resend.dev';
    
    // Normalize recipients to array
    const to = Array.isArray(options.to) ? options.to : [options.to];
    const cc = options.cc ? (Array.isArray(options.cc) ? options.cc : [options.cc]) : undefined;
    const bcc = options.bcc ? (Array.isArray(options.bcc) ? options.bcc : [options.bcc]) : undefined;

    const emailPayload: any = {
      from: fromEmail,
      to,
      subject: options.subject,
      html: options.html || options.text || '',
      ...(options.text && { text: options.text }),
      ...(options.replyTo && { reply_to: options.replyTo }),
      ...(cc && { cc }),
      ...(bcc && { bcc }),
    };

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Resend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message || 'Failed to send email');
    }

    return successResponse(undefined);
  } catch (error) {
    console.error('[emailService] Error sending email:', error);
    return errorResponse(handleServiceError(error, 'emailService.sendEmail', { options }));
  }
};

/**
 * Send contact form notification email to admin
 */
export const sendContactFormNotification = async (
  submission: { name: string; email: string; subject: string; message: string; phone?: string },
  adminEmail: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">New Contact Form Submission</h2>
      <div style="background: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <p><strong>Name:</strong> ${escapeHtml(submission.name)}</p>
        <p><strong>Email:</strong> <a href="mailto:${escapeHtml(submission.email)}">${escapeHtml(submission.email)}</a></p>
        ${submission.phone ? `<p><strong>Phone:</strong> ${escapeHtml(submission.phone)}</p>` : ''}
        <p><strong>Subject:</strong> ${escapeHtml(submission.subject)}</p>
      </div>
      <div style="margin: 20px 0;">
        <h3 style="color: #2563eb;">Message:</h3>
        <p style="white-space: pre-wrap;">${escapeHtml(submission.message)}</p>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: adminEmail,
    subject: `New Contact Form: ${submission.subject}`,
    html,
    replyTo: submission.email,
  });
};

/**
 * Send contact form auto-reply to user
 */
export const sendContactFormAutoReply = async (
  submission: { name: string; email: string },
  organizationName: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">Thank You for Contacting Us</h2>
      <p>Dear ${escapeHtml(submission.name)},</p>
      <p>Thank you for reaching out to ${escapeHtml(organizationName)}. We have received your message and will get back to you as soon as possible.</p>
      <p>We appreciate your interest and look forward to connecting with you.</p>
      <p>Best regards,<br>The ${escapeHtml(organizationName)} Team</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: submission.email,
    subject: `Thank you for contacting ${organizationName}`,
    html,
  });
};

/**
 * Send event registration confirmation email
 */
export const sendEventRegistrationConfirmation = async (
  registration: { name: string; email: string; eventTitle: string; eventDate: string; eventTime: string; eventLocation: string },
  organizationName: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">Event Registration Confirmed</h2>
      <p>Dear ${escapeHtml(registration.name)},</p>
      <p>Your registration for the following event has been confirmed:</p>
      <div style="background: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #2563eb;">${escapeHtml(registration.eventTitle)}</h3>
        <p><strong>Date:</strong> ${escapeHtml(registration.eventDate)}</p>
        <p><strong>Time:</strong> ${escapeHtml(registration.eventTime)}</p>
        <p><strong>Location:</strong> ${escapeHtml(registration.eventLocation)}</p>
      </div>
      <p>We look forward to seeing you there!</p>
      <p>If you need to cancel your registration, please contact us as soon as possible.</p>
      <p>Best regards,<br>The ${escapeHtml(organizationName)} Team</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: registration.email,
    subject: `Registration Confirmed: ${registration.eventTitle}`,
    html,
  });
};

/**
 * Send form submission notification (generic)
 */
export const sendFormSubmissionNotification = async (
  formName: string,
  submission: Record<string, any>,
  recipientEmail: string,
  formConfig?: { emailSubject?: string; emailTemplate?: string }
): Promise<ServiceResponse<void>> => {
  // Build email content from form data
  const fieldsHtml = Object.entries(submission)
    .map(([key, value]) => {
      const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
      return `<p><strong>${escapeHtml(displayKey)}:</strong> ${escapeHtml(displayValue)}</p>`;
    })
    .join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">New Form Submission: ${escapeHtml(formName)}</h2>
      <div style="background: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
        ${fieldsHtml}
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: recipientEmail,
    subject: formConfig?.emailSubject || `New Submission: ${formName}`,
    html: formConfig?.emailTemplate || html,
  });
};

/**
 * Send donation notification to admin
 */
export const sendDonationNotification = async (
  donation: {
    firstName: string;
    lastName: string;
    email: string;
    amount: number;
    type: string;
    donorLevel?: string;
    anonymous: boolean;
  },
  recipientEmail: string
): Promise<ServiceResponse<void>> => {
  const donorName = donation.anonymous 
    ? 'Anonymous Donor' 
    : `${donation.firstName} ${donation.lastName}`;
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">New Donation Received</h2>
      <div style="background: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <p><strong>Donor:</strong> ${escapeHtml(donorName)}</p>
        <p><strong>Email:</strong> ${escapeHtml(donation.email)}</p>
        <p><strong>Amount:</strong> $${donation.amount.toFixed(2)}</p>
        <p><strong>Type:</strong> ${escapeHtml(donation.type)}</p>
        ${donation.donorLevel ? `<p><strong>Donor Level:</strong> ${escapeHtml(donation.donorLevel)}</p>` : ''}
        <p><strong>Anonymous:</strong> ${donation.anonymous ? 'Yes' : 'No'}</p>
      </div>
      <p style="color: #666; font-size: 14px;">Please process this donation in the admin dashboard.</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: recipientEmail,
    subject: `New Donation: $${donation.amount.toFixed(2)} from ${donorName}`,
    html,
  });
};

/**
 * Send donation thank you email to donor
 */
export const sendDonationThankYou = async (
  donation: {
    firstName: string;
    email: string;
    amount: number;
    type: string;
    donorLevel?: string;
  },
  organizationName: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">Thank You for Your Generous Donation!</h2>
      <p>Dear ${escapeHtml(donation.firstName)},</p>
      <p>On behalf of ${escapeHtml(organizationName)}, we want to express our heartfelt gratitude for your generous donation of <strong>$${donation.amount.toFixed(2)}</strong>.</p>
      ${donation.donorLevel ? `<p>Your contribution qualifies you as a <strong>${escapeHtml(donation.donorLevel)}</strong>, and we're honored to have your support.</p>` : ''}
      <p>Your donation type: <strong>${escapeHtml(donation.type)}</strong></p>
      <p>Your support makes a meaningful difference in our community and helps us continue our important work.</p>
      <p>You will receive a receipt for your donation for tax purposes. If you have any questions, please don't hesitate to reach out.</p>
      <p style="margin-top: 30px;">With sincere appreciation,<br>The ${escapeHtml(organizationName)} Team</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: donation.email,
    subject: `Thank You for Your Donation to ${organizationName}`,
    html,
  });
};

/**
 * Helper function to escape HTML
 */
const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') return String(text);
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Send membership application notification to admin
 */
export const sendMembershipApplicationNotification = async (
  application: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    organization?: string;
    membershipType?: string;
    membershipLength?: string;
  },
  recipientEmail: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">New Membership Application Received</h2>
      <div style="background: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <p><strong>Name:</strong> ${escapeHtml(application.firstName)} ${escapeHtml(application.lastName)}</p>
        <p><strong>Email:</strong> ${escapeHtml(application.email)}</p>
        <p><strong>Phone:</strong> ${escapeHtml(application.phone)}</p>
        ${application.organization ? `<p><strong>Organization:</strong> ${escapeHtml(application.organization)}</p>` : ''}
        ${application.membershipType ? `<p><strong>Membership Type:</strong> ${escapeHtml(application.membershipType)}</p>` : ''}
        ${application.membershipLength ? `<p><strong>Membership Length:</strong> ${escapeHtml(application.membershipLength)}</p>` : ''}
      </div>
      <p style="color: #666; font-size: 14px;">Please review this application in the admin dashboard.</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: recipientEmail,
    subject: `New Membership Application: ${application.firstName} ${application.lastName}`,
    html,
  });
};

/**
 * Send membership application confirmation to applicant
 */
export const sendMembershipApplicationConfirmation = async (
  application: {
    firstName: string;
    email: string;
    membershipType?: string;
  },
  organizationName: string
): Promise<ServiceResponse<void>> => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <h2 style="color: #2563eb;">Membership Application Received</h2>
      <p>Dear ${escapeHtml(application.firstName)},</p>
      <p>Thank you for your interest in becoming a member of ${escapeHtml(organizationName)}!</p>
      <p>We have received your membership application${application.membershipType ? ` for ${escapeHtml(application.membershipType)} membership` : ''} and will review it shortly.</p>
      <p>You will receive an email notification once your application has been reviewed. If you have any questions in the meantime, please don't hesitate to contact us.</p>
      <p style="margin-top: 30px;">Best regards,<br>The ${escapeHtml(organizationName)} Team</p>
    </body>
    </html>
  `;

  return sendEmail({
    to: application.email,
    subject: `Membership Application Received - ${organizationName}`,
    html,
  });
};

export const emailService = {
  sendEmail,
  sendContactFormNotification,
  sendContactFormAutoReply,
  sendEventRegistrationConfirmation,
  sendFormSubmissionNotification,
  sendDonationNotification,
  sendDonationThankYou,
  sendMembershipApplicationNotification,
  sendMembershipApplicationConfirmation,
};

